import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminNav from "../../components/AdminNav";
import "../admin-ui.css";

type ResultType = "test" | "task";
type ResultStatus = "passed" | "failed" | "incomplete" | "notStarted";

type Result = {
  id?: string;
  username: string;
  fullName?: string;
  specialty?: string;
  type?: ResultType | string;
  date: string | null;
  finishedAt?: string | null;
  score: number | null;
  total: number | null;
  attemptId?: string;
  taskAttemptId?: string;
  taskSessionId?: string;
  sessionId?: string | null;
  completedTasks?: number;
  totalTasks?: number;
  isComplete?: boolean;
};

type ExamSession = {
  sessionId: string;
  name: string;
  specialty: string;
  assignmentType: ResultType;
};

const PASSING_PERCENT = 70;
const MOSCOW_TIME_ZONE = "Europe/Moscow";

const getMoscowDateParts = (value: string | number | Date) => {
  const parts = new Intl.DateTimeFormat("ru-RU", {
    timeZone: MOSCOW_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));

  return {
    year: parts.find((part) => part.type === "year")?.value || "",
    month: parts.find((part) => part.type === "month")?.value || "",
    day: parts.find((part) => part.type === "day")?.value || "",
  };
};

const formatMoscowDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("ru-RU", { timeZone: MOSCOW_TIME_ZONE })
    : "";

const normalizeType = (type?: string): ResultType =>
  type === "task" ? "task" : "test";

const getTypeText = (result: Result) =>
  normalizeType(result.type) === "task"
    ? "Ситуационные задачи"
    : "Тестирование";

const getResultDate = (result: Result) => result.finishedAt || result.date;

const getResultTimestamp = (result: Result) => {
  const date = getResultDate(result);
  const timestamp = date ? new Date(date).getTime() : 0;

  return Number.isFinite(timestamp) ? timestamp : 0;
};

const getDateInputValue = (result: Result) => {
  const timestamp = getResultTimestamp(result);

  if (!timestamp) {
    return "";
  }

  const { year, month, day } = getMoscowDateParts(timestamp);

  return `${year}-${month}-${day}`;
};

const hasCompletedResult = (result: Result) =>
  result.score !== null && result.total !== null && Boolean(getResultDate(result));

const getResultText = (result: Result) =>
  hasCompletedResult(result) ? `${result.score} / ${result.total}` : "?";

const getPercentValue = (result: Result) =>
  hasCompletedResult(result) && result.total
    ? ((result.score || 0) / result.total) * 100
    : null;

const getPercentText = (result: Result) => {
  const percent = getPercentValue(result);

  return percent === null ? "?" : `${percent.toFixed(1)}%`;
};
const isIncompleteResult = (result: Result) => result.isComplete === false;

const getStatusKey = (result: Result): ResultStatus => {
  if (isIncompleteResult(result)) {
    return "incomplete";
  }

  if (!hasCompletedResult(result)) {
    return "notStarted";
  }

  return (getPercentValue(result) || 0) >= PASSING_PERCENT ? "passed" : "failed";
};

const statusTextByKey: Record<ResultStatus, string> = {
  passed: "Сдано",
  failed: "Не сдано",
  incomplete: "Не завершено",
  notStarted: "Не проходил",
};

const statusClassNameByKey: Record<ResultStatus, string> = {
  passed: "admin-status-passed",
  failed: "admin-status-failed",
  incomplete: "admin-status-incomplete",
  notStarted: "admin-status-not-started",
};

const getStatusText = (result: Result) => statusTextByKey[getStatusKey(result)];

const getStatusClassName = (result: Result) =>
  statusClassNameByKey[getStatusKey(result)];

const getPercentClassName = (result: Result) => {
  const percent = getPercentValue(result);

  if (percent === null) {
    return "admin-percent-empty";
  }

  if (percent >= PASSING_PERCENT) {
    return "admin-percent-good";
  }

  return percent >= 50 ? "admin-percent-warning" : "admin-percent-bad";
};

const getDateText = (result: Result) => {
  const date = getResultDate(result);

  return date
    ? formatMoscowDateTime(date)
    : "Вход не зафиксирован";
};

const getActionLink = (result: Result, adminBasePath: string) => {
  const type = normalizeType(result.type);

  if (!result.attemptId) {
    return <span className="admin-muted">?</span>;
  }

  if (type === "task") {
    return (
      <Link
        className="admin-link-button"
        to={`${adminBasePath}/task-review?attemptId=${encodeURIComponent(
          result.attemptId
        )}`}
      >
        Просмотр
      </Link>
    );
  }

  return (
    <a
      className="admin-link-button"
      href={`/api/test-protocol/${encodeURIComponent(result.attemptId)}/pdf`}
      target="_blank"
      rel="noreferrer"
    >
      PDF
    </a>
  );
};

const AdminResultsPage: React.FC = () => {
  const [results, setResults] = useState<Result[]>([]);
  const [search, setSearch] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | ResultType>("all");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [examSessions, setExamSessions] = useState<ExamSession[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState<"date" | "percent" | "name">("date");
  const [deleteTarget, setDeleteTarget] = useState<Result | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isDeletingStudent, setIsDeletingStudent] = useState(false);
  const [message, setMessage] = useState("");
  const role = localStorage.getItem("role") || "";
  const username = localStorage.getItem("username") || "";
  const isSuperadmin = role === "superadmin";
  const adminBasePath = isSuperadmin ? "/superadmin" : "/admin";

  const loadResults = () => {
    const query =
      sessionFilter && sessionFilter !== "all"
        ? `?sessionId=${encodeURIComponent(sessionFilter)}`
        : "";

    fetch(`/api/results${query}`)
      .then((res) => res.json())
      .then((data: Result[]) => setResults(data))
      .catch((err) => console.error("Ошибка загрузки результатов:", err));
  };

  const loadExamSessions = () => {
    fetch("/api/exam-sessions")
      .then((res) => res.json())
      .then((data: ExamSession[]) => setExamSessions(Array.isArray(data) ? data : []))
      .catch((err) => console.error("EXAM SESSIONS LOAD ERROR", err));
  };

  useEffect(() => {
    loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionFilter]);

  useEffect(() => {
    loadExamSessions();
  }, []);

  const specialties = Array.from(
    new Set(results.map((result) => result.specialty || "").filter(Boolean))
  );

  const filteredResults = results
    .filter((result) => {
      const query = search.trim().toLowerCase();
      const resultType = normalizeType(result.type);
      const matchesSearch =
        !query ||
        (result.fullName || "").toLowerCase().includes(query) ||
        result.username.toLowerCase().includes(query);
      const matchesSpecialty =
        !specialtyFilter || result.specialty === specialtyFilter;
      const matchesType = typeFilter === "all" || resultType === typeFilter;
      const matchesSession =
        sessionFilter === "all" ||
        (sessionFilter === "none"
          ? !result.sessionId
          : result.sessionId === sessionFilter);
      const resultDateValue = getDateInputValue(result);
      const matchesDateFrom =
        !dateFrom || (Boolean(resultDateValue) && resultDateValue >= dateFrom);
      const matchesDateTo =
        !dateTo || (Boolean(resultDateValue) && resultDateValue <= dateTo);

      return (
        matchesSearch &&
        matchesSpecialty &&
        matchesType &&
        matchesSession &&
        matchesDateFrom &&
        matchesDateTo
      );
    })
    .sort((a, b) => {
      const aCompleted = hasCompletedResult(a);
      const bCompleted = hasCompletedResult(b);

      if (aCompleted !== bCompleted) {
        return aCompleted ? -1 : 1;
      }

      if (sortBy === "name") {
        return (a.fullName || a.username).localeCompare(
          b.fullName || b.username,
          "ru"
        );
      }

      if (sortBy === "percent") {
        const aPercent = getPercentValue(a) ?? -1;
        const bPercent = getPercentValue(b) ?? -1;

        if (aPercent !== bPercent) {
          return bPercent - aPercent;
        }
      }

      return getResultTimestamp(b) - getResultTimestamp(a);
    });

  const summaryItems = [
    {
      key: "total",
      label: "Всего",
      value: filteredResults.length,
      className: "admin-summary-total",
    },
    {
      key: "passed",
      label: statusTextByKey.passed,
      value: filteredResults.filter((result) => getStatusKey(result) === "passed")
        .length,
      className: "admin-summary-passed",
    },
    {
      key: "failed",
      label: statusTextByKey.failed,
      value: filteredResults.filter((result) => getStatusKey(result) === "failed")
        .length,
      className: "admin-summary-failed",
    },
    {
      key: "incomplete",
      label: statusTextByKey.incomplete,
      value: filteredResults.filter(
        (result) => getStatusKey(result) === "incomplete"
      ).length,
      className: "admin-summary-incomplete",
    },
    {
      key: "notStarted",
      label: statusTextByKey.notStarted,
      value: filteredResults.filter(
        (result) => getStatusKey(result) === "notStarted"
      ).length,
      className: "admin-summary-not-started",
    },
  ];

  const handleExportResults = () => {
    const rows = filteredResults.map((result, index) => ({
      "№": index + 1,
      "ФИО": result.fullName || "",
      "Логин": result.username,
      "Специальность": result.specialty || "",
      "Тип": getTypeText(result),
      "Поток":
        examSessions.find((session) => session.sessionId === result.sessionId)?.name ||
        (result.sessionId ? result.sessionId : "Без потока"),
      "Дата/время завершения": getDateText(result),
      "Балл": getResultText(result),
      "%": getPercentText(result),
      "Статус": getStatusText(result),
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 36 },
      { wch: 18 },
      { wch: 28 },
      { wch: 24 },
      { wch: 32 },
      { wch: 24 },
      { wch: 14 },
      { wch: 10 },
      { wch: 14 },
    ];
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Результаты");
    XLSX.writeFile(workbook, "results.xlsx");
  };

  const handleDeleteResult = async () => {
    if (!deleteTarget?.id) return;

    setIsDeleting(true);
    setMessage("");

    try {
      const response = await fetch(
        `/api/admin/results/${encodeURIComponent(deleteTarget.id)}`,
        {
          method: "DELETE",
          headers: {
            "X-User-Role": role,
            "X-Username": username,
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить результат");
      }

      setDeleteTarget(null);
      setMessage("Результат успешно удален. Копия сохранена в архив.");
      loadResults();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Не удалось удалить результат"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAllResults = async () => {
    if (!window.confirm("Удалить все результаты? Копия будет сохранена в архив.")) {
      return;
    }

    setIsDeletingAll(true);
    setMessage("");

    try {
      const response = await fetch("/api/admin/results", {
        method: "DELETE",
        headers: {
          "X-User-Role": role,
          "X-Username": username,
        },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить все результаты");
      }

      setMessage(
        `Удалено результатов: ${data.deletedCount || 0}. ${
          data.archivePath ? `Копия сохранена: ${data.archivePath}` : ""
        }`
      );
      loadResults();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Не удалось удалить все результаты"
      );
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleDeleteStudent = async (result: Result) => {
    const studentName = result.fullName || result.username;

    if (
      !window.confirm(
        `Удалить студента "${studentName}" из активных пользователей и текущего списка?`
      )
    ) {
      return;
    }

    setIsDeletingStudent(true);
    setMessage("");

    try {
      const params = new URLSearchParams({ type: normalizeType(result.type) });
      const response = await fetch(
        `/api/admin/students/${encodeURIComponent(result.username)}?${params.toString()}`,
        {
          method: "DELETE",
          headers: {
            "X-User-Role": role,
            "X-Username": username,
          },
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || "Не удалось удалить студента");
      }

      setMessage("Студент удалён");
      loadResults();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Не удалось удалить студента");
    } finally {
      setIsDeletingStudent(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-inner">
        <AdminNav section="results" />

        <h2>Результаты</h2>

        <div className="admin-card admin-toolbar" style={{ marginTop: "20px" }}>
          <input
            type="text"
            placeholder="Поиск по ФИО или логину"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ minWidth: "260px" }}
          />

          <select
            value={specialtyFilter}
            onChange={(event) => setSpecialtyFilter(event.target.value)}
            style={{ minWidth: "220px" }}
          >
            <option value="">Все специальности</option>
            {specialties.map((specialty) => (
              <option key={specialty} value={specialty}>
                {specialty}
              </option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(event) =>
              setTypeFilter(event.target.value as "all" | ResultType)
            }
            style={{ minWidth: "220px" }}
          >
            <option value="all">Все</option>
            <option value="test">Тестирование</option>
            <option value="task">Ситуационные задачи</option>
          </select>

          <select
            value={sessionFilter}
            onChange={(event) => setSessionFilter(event.target.value)}
            style={{ minWidth: "240px" }}
          >
            <option value="all">Все потоки</option>
            <option value="none">Без потока</option>
            {examSessions.map((session) => (
              <option key={session.sessionId} value={session.sessionId}>
                {session.name}
              </option>
            ))}
          </select>

          <label className="admin-filter-field">
            <span>Дата от</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </label>

          <label className="admin-filter-field">
            <span>Дата до</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </label>

          <select
            value={sortBy}
            onChange={(event) =>
              setSortBy(event.target.value as "date" | "percent" | "name")
            }
            style={{ minWidth: "190px" }}
          >
            <option value="date">Сначала новые</option>
            <option value="percent">По проценту</option>
            <option value="name">?? ???</option>
          </select>

          <button
            className="admin-button admin-button-primary"
            onClick={handleExportResults}
            disabled={filteredResults.length === 0}
          >
            Скачать результаты
          </button>
          {isSuperadmin && (
            <button
              className="admin-button admin-button-danger"
              type="button"
              onClick={handleDeleteAllResults}
              disabled={results.length === 0 || isDeletingAll}
            >
              Удалить все результаты
            </button>
          )}
        </div>

        {message && <div className="admin-card admin-message">{message}</div>}

        <div className="admin-summary-grid" aria-label="Сводка результатов">
          {summaryItems.map((item) => (
            <div
              className={`admin-summary-card ${item.className}`}
              key={item.key}
            >
              <div className="admin-summary-value">{item.value}</div>
              <div className="admin-summary-label">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>???</th>
                <th>Специальность</th>
                <th>Тип</th>
                <th>Дата/время завершения</th>
                <th>Балл</th>
                <th>%</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>

            <tbody>
              {filteredResults.length === 0 ? (
                <tr>
                  <td colSpan={8} className="admin-table-empty">
                    Нет результатов
                  </td>
                </tr>
              ) : (
                filteredResults.map((result, index) => (
                  <tr
                    key={`${
                      result.id ||
                      result.taskSessionId ||
                      result.attemptId ||
                      `${result.username}-${result.type || "test"}`
                    }-${index}`}
                  >
                    <td>{result.fullName || ""}</td>
                    <td>{result.specialty || ""}</td>
                    <td>
                      <span
                        className={`admin-badge admin-badge-${normalizeType(
                          result.type
                        )}`}
                      >
                        {getTypeText(result)}
                      </span>
                    </td>
                    <td>{getDateText(result)}</td>
                    <td>{getResultText(result)}</td>
                    <td>
                      <span
                        className={`admin-percent-badge ${getPercentClassName(
                          result
                        )}`}
                      >
                        {getPercentText(result)}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-badge ${getStatusClassName(result)}`}>
                        {getStatusText(result)}
                      </span>
                    </td>
                    <td>
                      <div className="admin-actions">
                        {isSuperadmin && getStatusKey(result) === "notStarted" ? (
                          <button
                            className="admin-button admin-button-danger admin-button-small"
                            type="button"
                            onClick={() => handleDeleteStudent(result)}
                            disabled={isDeletingStudent}
                          >
                            Удалить студента
                          </button>
                        ) : (
                          getActionLink(result, adminBasePath)
                        )}
                        {isSuperadmin && result.id && (
                          <button
                            className="admin-button admin-button-danger admin-button-small"
                            type="button"
                            onClick={() => setDeleteTarget(result)}
                          >
                            Удалить
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {deleteTarget && (
        <div className="admin-modal-backdrop" role="presentation">
          <div className="admin-modal" role="dialog" aria-modal="true">
            <h3>Удаление результата</h3>
            <p>Вы действительно хотите удалить этот результат?</p>

            <dl className="admin-details-list">
              <div>
                <dt>???</dt>
                <dd>{deleteTarget.fullName || "Не указано"}</dd>
              </div>
              <div>
                <dt>Специальность</dt>
                <dd>{deleteTarget.specialty || "Не указана"}</dd>
              </div>
              <div>
                <dt>Тип</dt>
                <dd>{getTypeText(deleteTarget)}</dd>
              </div>
              <div>
                <dt>Дата прохождения</dt>
                <dd>{getDateText(deleteTarget)}</dd>
              </div>
              <div>
                <dt>Балл</dt>
                <dd>{getResultText(deleteTarget)}</dd>
              </div>
            </dl>

            <div className="admin-modal-actions">
              <button
                className="admin-button"
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button
                className="admin-button admin-button-danger"
                type="button"
                onClick={handleDeleteResult}
                disabled={isDeleting}
              >
                {isDeleting ? "Удаление..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResultsPage;

